// Markdown-подмножество текста сообщения (FR-MSG-02) — ВЛАДЕЛЕЦ ПАРСИНГА = design-system (R2-G).
// Поддержка: **жирный**, *курсив* / _курсив_, `моноширинный`, ```блок кода```, [ссылки](url),
// автолинковка голых URL, упоминания @user (FR-MSG-09). БЕЗ unfurl (D-03) — ссылка лишь кликабельна.
//
// Парсер выдаёт ДЕРЕВО токенов (не HTML-строку) — MessageBubble рендерит их в безопасные React-узлы
// (никакого dangerouslySetInnerHTML, нулевой XSS-вектор). Чистая функция, без зависимостей и DOM.
// Допустимые схемы автолинковки/ссылок — только http/https/mailto (без javascript: и пр.).
const SAFE_SCHEME = /^(https?:\/\/|mailto:)/i;
/** Нормализует href ссылки: пустой/небезопасный → '#'. */
function safeHref(raw) {
    const href = raw.trim();
    if (SAFE_SCHEME.test(href))
        return href;
    // голый домен без схемы → достраиваем https://
    if (/^[\w-]+(\.[\w-]+)+(\/\S*)?$/.test(href))
        return `https://${href}`;
    return '#';
}
// Регэксп голого URL и упоминания для инлайн-скана.
const URL_RE = /\bhttps?:\/\/[^\s<>()]+[^\s<>().,!?:;'"]/gi;
const MENTION_RE = /(^|[^\w@])@([a-z0-9_.-]{2,32})/gi;
/**
 * Инлайн-парсинг одной строки в узлы. Порядок: сначала разбираем явные конструкции с разделителями
 * (code, bold, italic, [label](href)) рекурсивно по приоритету, затем в чистых текстовых кусках
 * автолинкуем URL и выделяем @mentions.
 */
function parseInline(input) {
    const nodes = [];
    let i = 0;
    let plain = '';
    const flushPlain = () => {
        if (plain.length > 0) {
            nodes.push(...autolink(plain));
            plain = '';
        }
    };
    while (i < input.length) {
        const ch = input[i];
        // inline code `…` — внутри ничего не форматируется
        if (ch === '`') {
            const end = input.indexOf('`', i + 1);
            if (end > i) {
                flushPlain();
                nodes.push({ type: 'code', value: input.slice(i + 1, end) });
                i = end + 1;
                continue;
            }
        }
        // bold **…** (двойная звёздочка)
        if (ch === '*' && input[i + 1] === '*') {
            const end = input.indexOf('**', i + 2);
            if (end > i) {
                flushPlain();
                nodes.push({ type: 'bold', children: parseInline(input.slice(i + 2, end)) });
                i = end + 2;
                continue;
            }
        }
        // italic *…* или _…_ (одиночный разделитель)
        if (ch === '*' || ch === '_') {
            const end = input.indexOf(ch, i + 1);
            if (end > i + 1) {
                flushPlain();
                nodes.push({ type: 'italic', children: parseInline(input.slice(i + 1, end)) });
                i = end + 1;
                continue;
            }
        }
        // ссылка [label](href)
        if (ch === '[') {
            const close = input.indexOf(']', i + 1);
            if (close > i && input[close + 1] === '(') {
                const paren = input.indexOf(')', close + 2);
                if (paren > close) {
                    flushPlain();
                    const label = input.slice(i + 1, close);
                    const href = safeHref(input.slice(close + 2, paren));
                    nodes.push({ type: 'link', href, label });
                    i = paren + 1;
                    continue;
                }
            }
        }
        plain += ch;
        i++;
    }
    flushPlain();
    return nodes;
}
/** В чистом тексте автолинкуем URL и выделяем @mentions (FR-LINKS-01 / FR-MSG-09). */
function autolink(text) {
    // Сначала URL, затем в оставшемся тексте — упоминания.
    const out = [];
    let last = 0;
    URL_RE.lastIndex = 0;
    let m;
    while ((m = URL_RE.exec(text)) !== null) {
        if (m.index > last)
            out.push(...splitMentions(text.slice(last, m.index)));
        out.push({ type: 'link', href: safeHref(m[0]), label: m[0] });
        last = m.index + m[0].length;
    }
    if (last < text.length)
        out.push(...splitMentions(text.slice(last)));
    return out;
}
/** Выделение @mentions в чистом тексте. */
function splitMentions(text) {
    const out = [];
    let last = 0;
    MENTION_RE.lastIndex = 0;
    let m;
    while ((m = MENTION_RE.exec(text)) !== null) {
        const lead = m[1] ?? '';
        const handle = m[2] ?? '';
        const start = m.index + lead.length;
        if (start > last)
            out.push({ type: 'text', value: text.slice(last, start) });
        out.push({ type: 'mention', handle });
        last = start + handle.length + 1; // +1 за символ '@'
    }
    if (last < text.length) {
        const tail = text.slice(last);
        if (tail.length > 0)
            out.push({ type: 'text', value: tail });
    }
    return out;
}
/**
 * Разбор полного текста сообщения в блоки. Блоки кода ```…``` (могут занимать несколько строк)
 * вырезаются первыми; остальное группируется в абзацы по двойному переносу, внутри — parseInline.
 */
export function parseMarkdown(text) {
    const blocks = [];
    const fence = /```([\s\S]*?)```/g;
    let cursor = 0;
    let m;
    while ((m = fence.exec(text)) !== null) {
        if (m.index > cursor)
            pushParagraphs(text.slice(cursor, m.index), blocks);
        blocks.push({ type: 'pre', value: (m[1] ?? '').replace(/^\n/, '').replace(/\n$/, '') });
        cursor = m.index + m[0].length;
    }
    if (cursor < text.length)
        pushParagraphs(text.slice(cursor), blocks);
    return blocks;
}
/** Текст без блоков кода → абзацы (двойной перенос строки) с инлайн-разбором. */
function pushParagraphs(text, into) {
    const parts = text.split(/\n{2,}/);
    for (const part of parts) {
        const trimmed = part.replace(/^\n+|\n+$/g, '');
        if (trimmed.length === 0)
            continue;
        into.push({ type: 'paragraph', children: parseInline(trimmed) });
    }
}
//# sourceMappingURL=markdown.js.map